/**
 * Tiny localStorage cache with a time-to-live, for values that are cheap to have
 * slightly stale.
 *
 * Why this exists: `MainLayoutComponent` is the `loadComponent` of every
 * top-level route, so it is destroyed and rebuilt on each hop between sections
 * (/businesses -> /individuals -> /branches ...). Its `ngOnInit` fires the three
 * sidebar pending-count reads every single time —
 *   GET /registration-requests?status=PENDING&limit=1
 *   GET /individual-registration-requests?status=PENDING&limit=1
 *   GET /branch/subscriptions?status=PENDING&limit=1
 * — three requests per navigation for numbers that change a few times a day.
 * With a TTL the badge paints instantly from the last known value and only one
 * navigation per window actually goes to the network.
 *
 * Everything here is best-effort. A missing, blocked or full localStorage
 * (private mode, quota, SSR) degrades to "no cache", i.e. the caller just
 * fetches like before — never to a thrown error.
 */

const PREFIX = 'waki.cache.';

/** How long a sidebar pending count stays good for. */
export const PENDING_COUNT_TTL_MS = 10 * 60 * 1000;

interface CacheEntry<T> {
  /** epoch ms at write time */
  t: number;
  v: T;
}

/**
 * Cache keys are scoped to the signed-in user, so a stale count can't bleed into
 * the next account on a shared browser if the session ends without a clean
 * logout (expired token, closed tab). Read straight from storage rather than
 * injecting AuthService — this must stay usable from plain functions.
 */
function scope(): string {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'anon';
    const user = JSON.parse(raw);
    const id = user?._id ?? user?.id ?? user?.email;
    return typeof id === 'string' && id ? id : 'anon';
  } catch {
    return 'anon';
  }
}

function storageKey(key: string): string {
  return `${PREFIX}${scope()}.${key}`;
}

/**
 * The cached value for `key`, or `null` when there is nothing usable — no entry,
 * older than `ttlMs`, or unparseable. A stale/corrupt entry is dropped on read
 * so it can't linger.
 *
 * `null` means "go fetch". Don't cache values whose own `null` is meaningful.
 */
export function readCached<T>(key: string, ttlMs: number): T | null {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    // A clock moved backwards would make `age` negative; treat that as fresh
    // rather than re-fetching on every navigation until the clock catches up.
    if (typeof entry?.t !== 'number' || Date.now() - entry.t > ttlMs) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    return entry.v ?? null;
  } catch {
    return null;
  }
}

/** Store `value` against `key`, stamped now. Silently no-ops if storage refuses. */
export function writeCached<T>(key: string, value: T): void {
  try {
    const entry: CacheEntry<T> = { t: Date.now(), v: value };
    localStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    /* quota / disabled storage — caching is optional, keep going */
  }
}

/** Drop one entry, e.g. after a mutation that invalidates it. */
export function clearCached(key: string): void {
  try {
    localStorage.removeItem(storageKey(key));
  } catch {
    /* nothing to do */
  }
}

/**
 * Drop every `waki.cache.*` entry, for all scopes. Called on logout so the next
 * user starts from the network.
 */
export function clearAllCached(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) doomed.push(key);
    }
    doomed.forEach(key => localStorage.removeItem(key));
  } catch {
    /* nothing to do */
  }
}
